// frontend/src/pages/GothamPotteryPage.js

import React from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarBlank,
  CheckCircle,
  Fire,
  Target,
  LockSimpleOpen,
  MapPin,
  Globe,
  Megaphone,
  PushPin,
  SealCheck,
} from "phosphor-react";

import PreLoginHeader from "../components/PreLoginHeader";
import ActivityCard from "../components/ActivityCard";
import AvatarStack from "../components/AvatarStack";

/* -----------------------------------------------------------
   DEMO HELPERS
------------------------------------------------------------ */

const hoursAgo = (hours) => {
  const date = new Date();
  date.setHours(date.getHours() - hours);
  return date.toISOString();
};

/* -----------------------------------------------------------
   DEMO USERS
------------------------------------------------------------ */

const DEMO_USERS = {
  jade: {
    id: 9001,
    name: "Jade Kim",
    display_name: "Jade Kim",
    username: "thejadepillar",
    profile_pic_url: "/images/jadepropic1.jpg",
  },

  maya: {
    id: 9002,
    name: "Maya Rodriguez",
    display_name: "Maya Rodriguez",
    username: "mayamoves",
    profile_pic_url: "/images/mayapropic2.png",
  },

  reeves: {
    id: 9003,
    name: "Reeves Li",
    display_name: "Reeves Li",
    username: "reevesli1225",
    profile_pic_url: "/images/reevespropic1.jpg",
  },

  aaliyah: {
    id: 9004,
    name: "Aaliyah Harris",
    display_name: "Aaliyah Harris",
    username: "aharris214",
    profile_pic_url: "/images/aaliyahpropic1.jpg",
  },

  dhruv: {
    id: 9005,
    name: "Dhruv Chatterjee",
    display_name: "Dhruv Chatterjee",
    username: "dhruvchat",
    profile_pic_url: "/images/dhruvpropic1.jpg",
  },

  yuna: {
    id: 9006,
    name: "Yuna Moon",
    display_name: "Yuna Moon",
    username: "yunamoon",
    profile_pic_url: "/images/yunapropic1.jpg",
  },
};

const DEMO_MEMBERS = [
  DEMO_USERS.jade,
  DEMO_USERS.yuna,
  DEMO_USERS.dhruv,
  DEMO_USERS.aaliyah,
  DEMO_USERS.maya,
  DEMO_USERS.reeves,
];

/* -----------------------------------------------------------
   DEMO ACTIVITIES
------------------------------------------------------------ */

const GOTHAM_CIRCLE = [{ id: 8201, name: "Gotham Pottery" }];

const DEMO_ACTIVITIES = [
  {
    id: 7201,
    user_id: DEMO_USERS.jade.id,
    user: DEMO_USERS.jade,

    title: "Touching grass: episode 1",

    description:
      "When I was like 10, I used to beg my mom to take my sister and me to one of those paint-your-own-pottery places at SM Mall in Makati. A core memory from when I lived in Manila. So I wanted to try giving pottery a real go. Stay tuned for updates on calming our nervous systems @Yuna",
    mood: "Regulated-ish",
    duration: "2 hours",
    location: "Gotham Pottery, NYC",
    timestamp: hoursAgo(2),

    tags: ["pottery", "ceramics", "art", "crafts"],

    circles: GOTHAM_CIRCLE,

    media_urls: [
      "/images/potterybeg1.png",
      "/images/potterybeg2.png",
      "/images/potterybeg3.png",
    ],

    high_five_count: 18,
    has_high_fived: true,
    comment_count: 4,

    highfivers_preview: [
      DEMO_USERS.yuna,
      DEMO_USERS.maya,
      DEMO_USERS.aaliyah,
    ],

    commenters_preview: [
      DEMO_USERS.yuna,
      DEMO_USERS.maya,
    ],

    demo_comments: [
      {
        id: 9401,
        content: "The matching mugs are absolutely happening",
        created_at: hoursAgo(1.3),
        star_count: 3,
        starred_by_me: true,
        user: DEMO_USERS.yuna,
      },
      {
        id: 9402,
        content: "Wait now I want to try this",
        created_at: hoursAgo(0.7),
        star_count: 2,
        starred_by_me: false,
        user: DEMO_USERS.maya,
      },
    ],
  },

  {
    id: 7202,
    user_id: DEMO_USERS.yuna.id,
    user: DEMO_USERS.yuna,

    title: "Matcha bowl!",

    description:
      "Your Majesty, Paolo is exhausted because only Paolo can take THIS & THIS and give you... (swipe to see!) a Princess <3",

    mood: "Regal",
    duration: "2 hours",
    location: "Gotham Pottery, NYC",
    timestamp: hoursAgo(19),

    tags: ["pottery", "ceramics", "glazing"],

    circles: GOTHAM_CIRCLE,

    media_urls: [
      "/images/potterymatcha2.png",
      "/images/potterymatcha1.png",
    ],

    high_five_count: 31,
    has_high_fived: true,
    comment_count: 5,

    highfivers_preview: [
      DEMO_USERS.jade,
      DEMO_USERS.aaliyah,
      DEMO_USERS.reeves,
    ],

    commenters_preview: [
      DEMO_USERS.jade,
      DEMO_USERS.aaliyah,
    ],
  },

  {
    id: 7204,
    user_id: DEMO_USERS.maya.id,
    user: DEMO_USERS.maya,

    title: "Structural integrity: pending",

    description:
      "To quote the greatest person to ever put me to sleep, Bob Ross: “We don’t make mistakes, just happy little accidents.” I got the walls too thin and the whole thing folded in on itself about thirty seconds after this photo. Going back on Friday to try again.",

    mood: "Humbled",
    duration: "1 hour 45 minutes",
    location: "Gotham Pottery, NYC",
    timestamp: hoursAgo(67),

    tags: ["pottery", "wheelthrowing", "crafts"],

    circles: GOTHAM_CIRCLE,

    media_urls: [
      "/images/potteryfail1.png",
    ],

    high_five_count: 22,
    has_high_fived: true,
    comment_count: 6,

    highfivers_preview: [
      DEMO_USERS.dhruv,
      DEMO_USERS.jade,
      DEMO_USERS.yuna,
    ],

    commenters_preview: [
      DEMO_USERS.dhruv,
      DEMO_USERS.jade,
    ],
  },

  {
    id: 7203,
    user_id: DEMO_USERS.aaliyah.id,
    user: DEMO_USERS.aaliyah,

    title: "Thirty, flirty, and thriving",

    description:
      "Thank you friends for coming with me to make our own pottery champagne glasses. In this economy... yes, we made the glassware ourselves 🥂",

    mood: "Clink Clink",
    duration: "2 hours 30 minutes",
    location: "Gotham Pottery, NYC",
    timestamp: hoursAgo(42),

    tags: ["pottery", "ceramics"],

    circles: GOTHAM_CIRCLE,

    media_urls: [
      "/images/potterycheers1.png",
    ],

    high_five_count: 27,
    has_high_fived: false,
    comment_count: 3,

    highfivers_preview: [
      DEMO_USERS.maya,
      DEMO_USERS.yuna,
      DEMO_USERS.jade,
    ],

    commenters_preview: [
      DEMO_USERS.maya,
      DEMO_USERS.reeves,
    ],
  },

  {
    id: 7205,
    user_id: DEMO_USERS.reeves.id,
    user: DEMO_USERS.reeves,

    title: "10th class!",

    description:
      "We did it, Joe. Proud of us for making it to class 10. Grateful for the amazing instructors and pals!",
    mood: "Tenured",
    duration: "2 hours",
    location: "Gotham Pottery, NYC",
    timestamp: hoursAgo(93),

    tags: ["pottery", "art"],

    circles: GOTHAM_CIRCLE,

    media_urls: [
      "/images/potteryfriends1.png",
    ],

    high_five_count: 39,
    has_high_fived: true,
    comment_count: 7,

    highfivers_preview: [
      DEMO_USERS.aaliyah,
      DEMO_USERS.maya,
      DEMO_USERS.yuna,
      DEMO_USERS.jade,
    ],

    commenters_preview: [
      DEMO_USERS.aaliyah,
      DEMO_USERS.maya,
    ],
  },
];

/* -----------------------------------------------------------
   CIRCLE SLIM BANNER
------------------------------------------------------------ */

const CircleSlimPreviewBanner = () => {
  const tags = ["NYC", "Pottery", "Ceramics", "Art", "Community"];

  return (
    <section className="rounded-[30px] bg-blue-100 px-4 py-3 sm:px-6">
      <div className="flex min-w-0 items-center gap-4 sm:gap-6">
        <img
          src="/images/gothampottery2.png"
          alt="Gotham Pottery"
          className="
            h-20 w-20 shrink-0
            rounded-full object-cover
            sm:h-28 sm:w-28
          "
        />

        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-1.5">
            <h1
              className="
                truncate text-[16px] font-black
                tracking-[-0.03em] text-blue-700
                sm:text-[22px]
              "
            >
              Gotham Pottery
            </h1>

            <CheckCircle
              size={18}
              weight="fill"
              className="shrink-0 text-blue-400"
              aria-label="Verified"
            />
          </div>

          <p className="mt-1 max-w-[390px] text-sm leading-snug text-gray-700 sm:max-w-2xl">
            <span className="font-medium">
               “We don’t make mistakes, just happy little accidents.” - Bob Ross
            </span>
            <br />
            Beginner-friendly wheel throwing, hand building, and pottery painting with guidance from start to finish.
            <br />
            Perfect for beginners, date nights, and private events!
          </p>

          {/* Location + website */}
          <div className="mt-2 space-y-0 mb-2">
            <a
              href="https://www.google.com/maps/search/?api=1&query=Gotham+Pottery+New+York+NY"
              target="_blank"
              rel="noopener noreferrer"
              className="
                flex w-fit items-center gap-1
                text-xs text-gray-600
                transition hover:text-blue-700 hover:underline
                sm:text-sm
              "
            >
              <MapPin
                size={13}
                weight="duotone"
                className="shrink-0 text-blue-700"
              />
              <span>NoMad, New York</span>
            </a>

            <a
              href="https://www.gothampottery.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="
                flex w-fit items-center gap-1
                text-xs text-gray-600
                transition hover:text-blue-700 hover:underline
                sm:text-sm
              "
            >
              <Globe
                size={13}
                weight="duotone"
                className="shrink-0 text-blue-700"
              />
              <span>gothampottery.com</span>
            </a>
          </div>

          <div className="mt-1.5 flex flex-wrap gap-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="
                  rounded-full
                  bg-white/70
                  px-2.5 py-0.5
                  text-[11px]
                  font-semibold
                  text-blue-700
                "
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* -----------------------------------------------------------
   UPCOMING EVENT
------------------------------------------------------------ */

const UpcomingEventPanel = () => {
  const attendees = [
    {
      id: 1,
      name: "Maya",
      profile_pic_url: "/images/mayapropic2.png",
    },
    {
      id: 2,
      name: "Aaliyah",
      profile_pic_url: "/images/aaliyahpropic1.jpg",
    },
    {
      id: 3,
      name: "Yuna",
      profile_pic_url: "/images/yunapropic1.jpg",
    },
  ];

  return (
    <section className="rounded-2xl bg-white px-4 py-3.5">
      <div className="flex items-center justify-between">
        <h2 className="ml-1 text-lg font-semibold text-blue-600">
          Upcoming Events
        </h2>
      </div>

      <div
        className="
          mt-4 flex items-start gap-5
          rounded-3xl bg-blue-100
          px-3 pb-2 pt-3
        "
      >
        <div
          className="
            w-14 shrink-0
            rounded-2xl bg-white
            px-3 pb-3 pt-2
            text-center
            shadow-[0px_4px_0px_rgba(37,99,235,0.96)]
          "
        >
          <div className="-mb-1.5 text-lg font-semibold text-amber-500">
            21
          </div>

          <div className="text-sm font-bold text-gray-500">
            SEP
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-xs text-gray-600">
            Mon 6:30 PM
          </p>

          <h3 className="break-words text-sm font-medium leading-tight text-gray-900">
            21st Night of September: Open Studio
          </h3>

          <a
            href="https://www.google.com/maps/search/?api=1&query=Gotham+Pottery+New+York+NY"
            target="_blank"
            rel="noopener noreferrer"
            className="
              mt-1 flex w-fit items-start gap-0.5
              text-[12px] text-blue-600
              hover:underline
            "
          >
            <MapPin
              size={12}
              weight="duotone"
              className="mt-0.5 shrink-0"
            />
            <span>Gotham Pottery</span>
          </a>

          <p className="mt-1.5 whitespace-pre-wrap text-[13px] leading-tight text-gray-700">
            Do you remember?
            <span className="sm:hidden"> </span>
            <br className="hidden sm:block" />
            That we'll have Earth, Wind &amp; Fire on all night? Bring whatever
            you&apos;re working on and come throw, glaze, or just hang out!
          </p>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="
                inline-flex h-7
                !min-h-0 !min-w-0
                items-center justify-center
                overflow-hidden rounded-full
                bg-blue-700 px-3 py-1.5
                text-[13px] leading-none text-white
                transition hover:bg-blue-800
              "
            >
              Let&apos;s do it!
            </button>

            <button
              type="button"
              className="
                flex h-7
                !min-h-0 !min-w-0
                items-center justify-center
                overflow-hidden
              "
              aria-label="View attendees"
            >
              <AvatarStack users={attendees} />
            </button>

            <span className="text-[11px] font-medium text-gray-500">
              18 going
            </span>
          </div>

          <button
            type="button"
            className="
              mt-2
              !min-h-0 !min-w-0
              text-xs text-blue-700
              hover:underline
            "
          >
            View event
          </button>
        </div>
      </div>
    </section>
  );
};

/* -----------------------------------------------------------
   STUDIO GALLERY
------------------------------------------------------------ */

const StudioGallery = () => {
  const photos = [
    "/images/gotham0.png",
    "/images/gotham1.png",
    "/images/gotham2.png",
    "/images/gotham3.png",
  ];

  return (
    <section className="rounded-2xl bg-white p-4">
      <div className="mb-2 flex items-center justify-between px-1">
        <h3 className="text-lg font-semibold text-gray-900">
          Lately at the studio
        </h3>
      </div>

      <div
        className="
          no-scrollbar
          flex snap-x snap-mandatory
          gap-2 overflow-x-auto
          overscroll-x-contain
          pb-1
        "
      >
        {photos.map((src, index) => (
          <img
            key={src}
            src={src}
            alt={`Gotham Pottery studio ${index + 1}`}
            draggable="false"
            className="
              h-36 w-[82%] shrink-0
              snap-start rounded-2xl object-cover
              sm:h-40
            "
          />
        ))}
      </div>
    </section>
  );
};

/* -----------------------------------------------------------
   UPLOAD ACTIVITY
------------------------------------------------------------ */

const UploadActivityButton = ({ onAction }) => {
  return (
    <button
      type="button"
      onClick={onAction}
      className="
        w-full
        !min-h-0 !min-w-0
        rounded-3xl bg-blue-600
        px-3 py-2
        sm:py-2.5
        sm:mb-0 mb-3
        font-semibold text-white
        transition hover:bg-blue-700
      "
    >
      Upload Activity
    </button>
  );
};

/* -----------------------------------------------------------
   MEMBERS PANEL
------------------------------------------------------------ */

const MembersPanel = () => {
  return (
    <section className="rounded-2xl bg-white p-5 pb-3 pt-3">
      <h3 className="mb-2 text-lg font-semibold text-gray-900">
        Members
      </h3>

      <div className="flex -space-x-3">
        {DEMO_MEMBERS.slice(0, 4).map((member) => (
          <img
            key={member.id}
            src={member.profile_pic_url}
            alt={member.display_name}
            title={member.display_name}
            className="
              h-9 w-9 rounded-full
              border-2 border-white object-cover
            "
          />
        ))}
      </div>

      <button
        type="button"
        className="
          mt-2 ml-1
          !min-h-0 !min-w-0
          bg-transparent p-0
          text-sm text-gray-900
          transition
          hover:font-medium hover:text-blue-600
        "
      >
        248 members
      </button>
    </section>
  );
};

/* -----------------------------------------------------------
   PROGRESS PANEL
------------------------------------------------------------ */

const CircleProgressPanel = () => {
  const items = [
    {
      icon: CalendarBlank,
      label: "Classes this month",
      value: "4",
    },
    {
      icon: Fire,
      label: "Current Streak",
      value: "3 weeks",
    },
    {
      icon: Target,
      label: "August Goal",
      value: "4 of 5",
    },
    {
      icon: CheckCircle,
      label: "Next class",
      value: "Friday",
    },
  ];

  return (
    <section className="mt-4 rounded-2xl bg-white p-5">
      <p className="mb-1 px-1 text-[16px] font-black tracking-[-0.02em] text-blue-600">
        Your Progress
      </p>

      <div className="space-y-1">
        {items.map(({ icon: Icon, label, value }) => (
          <div
            key={label}
            className="flex items-center justify-between rounded-xl px-2 py-2.5 font-semibold"
          >
            <div className="flex min-w-0 items-center gap-2">
              <Icon
                size={17}
                weight="duotone"
                className="shrink-0 text-blue-600"
              />

              <span className="truncate text-sm text-gray-700">
                {label}
              </span>
            </div>

            <span className="ml-2 shrink-0 text-sm font-medium text-gray-500">
              {value}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
};

/* -----------------------------------------------------------
   ANNOUNCEMENT
------------------------------------------------------------ */

const AnnouncementBanner = () => {
  return (
    <section className="relative rounded-2xl bg-white p-5 pb-3 pt-3">
      <div className="absolute -top-3 left-5">
        <span
          className="
            inline-flex items-center gap-1.5
            rounded-full bg-blue-600
            px-3 py-1
            text-sm text-white
            shadow-sm
          "
        >
          <Megaphone
            size={14}
            weight="fill"
            className="text-white"
          />
          Announcements
        </span>
      </div>

      <div className="mt-1 sm:mb-0 mb-2 flex items-center justify-end">
        <div className="flex gap-1.5">
          <button
            type="button"
            className="
              inline-flex h-6
              !min-h-0 !min-w-0
              items-center justify-center
              overflow-hidden rounded-full
              bg-blue-100 px-3 py-1
              text-sm leading-none text-blue-700
              transition-colors
              hover:ring-2 hover:ring-blue-200
            "
            style={{ lineHeight: 1 }}
          >
            View all
          </button>

          <button
            type="button"
            className="
              inline-flex h-6
              !min-h-0 !min-w-0
              items-center justify-center
              overflow-hidden rounded-full
              bg-yellow-200 px-3 py-1
              text-sm leading-none text-black
              transition-colors
              hover:ring-2 hover:ring-yellow-300
            "
            style={{ lineHeight: 1 }}
          >
            Create
          </button>
        </div>
      </div>

      <button
        type="button"
        className="
          mt-0 block w-full
          !min-h-0 !min-w-0
          text-left
        "
      >
        <div className="break-words text-sm font-semibold leading-snug text-gray-900 md:text-[15px]">
          🍎 &nbsp;September Studio Challenge:
          <br className="md:hidden" />
          {" "}It’s Harvest Season
        </div>

        <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-snug text-gray-700">
          Build your own ceramic harvest, one week at a time
          <br />
          <strong>Week 1:</strong> The fruit bowl
          <br />
          <strong>Week 2:</strong> Apples + pears
          <br />
          <strong>Week 3:</strong> Figs + pomegranates
          <br />
          <strong>Week 4:</strong> Your pick!
          <br />
          Share your growing collection each week!{" "}
          <br className="md:hidden" />
          <a
            href="https://www.gothampottery.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-blue-700 hover:underline"
          >
            Find classes here
          </a>{" "}
          🍐
        </p>
      </button>
    </section>
  );
};

/* -----------------------------------------------------------
   CIRCLE STATUS PANEL
------------------------------------------------------------ */

const CircleStatusPanel = () => {
  return (
    <section className="rounded-2xl items-end bg-transparent px-3 sm:mb-0 -mb-1 sm:pt-3 pt-2">
      <div className="flex flex-wrap items-start justify-end gap-3 md:justify-start">
        <span
          className="
            inline-flex items-center gap-1
            rounded-full bg-gray-100
            !min-h-0 !min-w-0 !px-2 !py-0.5
            text-xs font-medium text-gray-700
          "
        >
          <LockSimpleOpen size={14} />
          Public
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center justify-end gap-2 md:justify-start">
        <button
          type="button"
          className="
            !min-h-0 !min-w-0
            rounded-full bg-blue-600
            sm:!px-3 sm:!py-1 px-2.5 py-1.5
            text-xs sm:text-sm leading-none text-white
            transition
            hover:ring-2 hover:ring-blue-400
          "
        >
          Invite
        </button>

        <button
          type="button"
          className="
            !min-h-0 !min-w-0
            rounded-full bg-blue-800
            sm:!px-3 sm:!py-1 px-2.5 py-1.5
            text-xs sm:text-sm font-medium leading-none text-white
            transition
            hover:ring-2 hover:ring-blue-400
          "
        >
          Join
        </button>
      </div>
    </section>
  );
};

/* -----------------------------------------------------------
   GOTHAM POTTERY DEMO PAGE
------------------------------------------------------------ */

function GothamPotteryPage({ embedded = false }) {
  const navigate = useNavigate();

  const handlePreviewAction = () => {
    navigate("/signup");
  };

  return (
    <div
      className={
        embedded
          ? "bg-blue-50 text-[#181411]"
          : "min-h-screen bg-blue-50 text-[#181411]"
      }
    >
      {!embedded && <PreLoginHeader />}

      {!embedded && (
        <section className="px-6 pb-6 pt-[4.45rem] text-center md:px-10 lg:pb-8 lg:pt-24">
          <h1 className="mx-auto max-w-3xl text-2xl font-black leading-[0.95] tracking-[-0.03em] text-gray-900 lg:text-[38px]">
            Your{" "}
            <span className="whitespace-nowrap text-blue-500">
              third place
            </span>{" "}
            starts here
          </h1>

          <p className="mx-auto mt-4 max-w-4xl text-sm leading-tight sm:tracking-[-0.02em] text-gray-600 sm:text-[20px]">
            Share progress, organize online and offline events,
            <br className="sm:hidden" />
            <br className="hidden sm:block" />
            {" "}celebrate every step,
            <br className="sm:hidden" />
            {" "}and turn hobbies into real community
          </p>
        </section>
      )}

      {/* Circle slim banner */}
      <div className="w-full bg-blue-50">
        <div className="mx-auto w-full max-w-[400px] sm:max-w-7xl px-5 sm:pb-4 pb-2 sm:px-6 md:px-10 xl:px-16">
          <CircleSlimPreviewBanner />
        </div>
      </div>

      {/* Circle page layout */}
      <main
        className="
          mx-auto w-full max-w-7xl
          px-4 pb-8
          sm:px-6 md:px-10 xl:px-16
        "
      >
        <div
          className="
            grid grid-cols-1 items-start gap-6
            md:grid-cols-[1fr_minmax(0,2fr)]
            min-[1100px]:grid-cols-[1fr_minmax(0,2fr)_1fr]
          "
        >
          {/* LEFT RAIL */}
          <aside className="hidden md:sticky md:top-4 md:block md:h-max">
            <div
              className="
                space-y-4 overflow-auto
                overscroll-contain no-scrollbar
              "
              style={{
                maxHeight: "calc(100dvh - 80px)",
              }}
            >
              <UpcomingEventPanel />
              <CircleProgressPanel />
            </div>
          </aside>

          {/* MIDDLE */}
          <section className="min-w-0 space-y-4">
            {/* Mobile status */}
            <div className="md:hidden">
              <CircleStatusPanel />
            </div>

            {/* Mobile event */}
            <div className="md:hidden">
              <UpcomingEventPanel />
            </div>

            {/* Mobile gallery */}
            <div className="md:hidden">
              <StudioGallery />
            </div>

            {/* Mobile members */}
            <div className="md:hidden">
              <MembersPanel />
            </div>

            {/* Mobile upload */}
            <div className="md:hidden">
              <UploadActivityButton
                onAction={handlePreviewAction}
              />
            </div>

            <AnnouncementBanner />

            <div
              className="
                space-y-4
                md:max-h-[calc(100dvh-180px)]
                md:overflow-auto
                md:pb-24 md:pr-1
                no-scrollbar
              "
            >
              {DEMO_ACTIVITIES.map((activity, index) => (
                <div
                  key={activity.id}
                  className="relative"
                  style={
                    index === 0
                      ? { marginTop: "1.5rem" }
                      : undefined
                  }
                >
                  {index === 0 && (
                    <span
                      className="
                        absolute -top-2 left-3 z-10
                        inline-flex items-center gap-1
                        rounded-full bg-sky-200
                        px-2 py-0.5
                        text-xs font-medium text-blue-800
                        shadow-[0_2px_0px_rgba(37,99,235,0.96)]
                      "
                    >
                      <PushPin
                        size={14}
                        weight="fill"
                      />
                      Pinned
                    </span>
                  )}

                  <ActivityCard
                    activity={activity}
                    currentUser={DEMO_USERS.jade}
                    readOnly
                    contentClassName="px-6 py-5"
                    onTagClick={handlePreviewAction}
                  />
                </div>
              ))}
            </div>
          </section>

          {/* RIGHT RAIL */}
          <aside className="hidden min-[1100px]:sticky min-[1100px]:top-4 min-[1100px]:block min-[1100px]:h-max">
            <div
              className="
                space-y-4 overflow-auto
                overscroll-contain pr-1 no-scrollbar
              "
              style={{
                maxHeight: "calc(100dvh - 80px)",
              }}
            >
              <CircleStatusPanel />

              {/* Swipeable Gotham gallery above Members */}
              <StudioGallery />

              <MembersPanel />

              <UploadActivityButton
                onAction={handlePreviewAction}
              />
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

export default GothamPotteryPage;